/* eslint-disable @typescript-eslint/no-misused-promises */
import React from "react"
import { TouchableOpacity, View } from "react-native"
import { type AnalysisType } from "@/generated/graphql"
import {
  PrimaryButton,
  SecondaryLightButton,
} from "@/components/buttons/CommonButtons"
import BFText, { Body, H3 } from "@/components/standard/BFText"
import Tooltip from "rn-tooltip"
import ToolTips from "@/constants/ToolTips"
import { BFColors } from "@/constants/Colors"
import InfoIcon from "@/assets/icons/tooltip-i-notfilled.svg"
import { useAnalyzeContext } from "@/components/analysis/AnalyzeContext"
import { useAnalysisData } from "@/hooks/useAnalysisData"

interface NoCompsInfoProps {
  analysis: AnalysisType
  onCalculateSelect: () => void
}

const NoCompsInfo: React.FC<NoCompsInfoProps> = ({
  analysis,
  onCalculateSelect,
}) => {
  const { defaultsEqualSelected } = useAnalysisData()
  const compsCount: number = analysis?.comps?.length || 0

  const { onCustomCompsPress } = useAnalyzeContext()

  // Render function when no comps are found on default filters
  const renderNoCompsFound = () => (
    <>
      <Body
        className="text-[15px] text-midnight-blue"
        style={{
          alignItems: "center",
          textAlign: "center",
          paddingTop: 26,
        }}
      >
        We couldn’t find any relevant comps.
      </Body>

      <Body
        style={{
          alignItems: "center",
          textAlign: "center",
          paddingTop: 10,
          width: 220,
          alignSelf: "center",
        }}
      >
        <BFText className="text-midnight-blue">
          Try customizing your comp set to see if there are more to view.
        </BFText>
      </Body>

      <View className="mt-10 self-center w-[200px] h-[10px]">
        <PrimaryButton
          testID="customize-comps"
          title={"Customize your Comps"}
          onPress={onCustomCompsPress}
        />
      </View>
    </>
  )

  // Render function when no comps are found with customized filters
  const renderCustomNoCompsFound = () => (
    <>
      <Body
        className="text-[15px] text-midnight-blue"
        style={{
          alignItems: "center",
          textAlign: "center",
          paddingTop: 16,
        }}
      >
        Still no comps.
      </Body>

      <Body
        className="text-midnight-blue"
        style={{
          alignItems: "center",
          textAlign: "center",
          marginTop: 10,
          width: 250,
          alignSelf: "center",
        }}
      >
        Don’t worry, you can still{" "}
        <TouchableOpacity
          testID={"calculate-link-button"}
          onPress={onCalculateSelect}
        >
          <BFText
            className="text-calm-blue underline pt-[8px]"
            style={{ position: "relative", top: 2 }}
          >
            calculate
          </BFText>
        </TouchableOpacity>{" "}
        your profit.
      </Body>

      <View className="p-4 rounded-lg">
        <View className="flex-row items-center justify-start mb-[6px]">
          <BFText className="text-calm-blue text-[15px] font-figtree-extrabold leading-tight tracking-tight mt-2">
            Did you know?
          </BFText>
        </View>

        <BFText className="max-w-[350px] h-32 p-3.5 text-lightest bg-calm-blue text-[11px] font-spacegrotesk rounded-md border border-cyan-800 justify-center items-center gap-2.5">
          Backflip currently supports comps for single family homes in the
          United States and comp coverage can vary in rural areas.{"\n"}
          {"\n"}
          We are constantly expanding our data coverage, so keep checking back.
        </BFText>
      </View>
    </>
  )

  return (
    <View style={{ flex: 1, justifyContent: "center", marginHorizontal: 10 }}>
      <View className="flex-row items-center p-4 pb-2 w-full">
        <H3>Comps</H3>

        <Tooltip
          popover={
            <BFText className="font-spacegrotesk text-white text-[12px] p-[2px]">
              {ToolTips.compsSelected}
            </BFText>
          }
          actionType="press"
          backgroundColor={BFColors.calmBlue}
          withOverlay={false}
          height={"auto"}
          width={250}
          containerStyle={{
            borderRadius: 2,
          }}
          toggleWrapperProps={{
            hitSlop: 32,
          }}
        >
          <View className="pl-1">
            <InfoIcon width={15} height={15} />
          </View>
        </Tooltip>
        <TouchableOpacity
          testID={"customize-comps-link-button"}
          onPress={onCustomCompsPress}
          hitSlop={16}
        >
          <BFText
            className={`text-[12px] font-spacegrotesk underline tracking-tight pb-0 ml-2 text-calm-blue`}
          >
            Customize Comps
          </BFText>
        </TouchableOpacity>
      </View>
      {compsCount <= 4 && defaultsEqualSelected
        ? renderNoCompsFound()
        : renderCustomNoCompsFound()}
    </View>
  )
}

export default NoCompsInfo

interface NoCompsAvailableColumnProps {
  onEnterArv: () => void
}

export const NoCompsAvailableColumn: React.FC<NoCompsAvailableColumnProps> = ({
  onEnterArv,
}) => {
  return (
    <View className="mr-[2px] items-center">
      <View className="w-[100px] mr-0 justify-center">
        <SecondaryLightButton
          testID={"enter-arv-button"}
          title="ENTER ARV"
          size="sm"
          onPress={onEnterArv}
        />
      </View>
      {/* Horz tool line */}
      <View className="w-[160px] h-px bg-neutral-300 my-[20px]" />

      <BFText className="text-black text-[12px] font-normal font-spacegrotesk leading-none tracking-tight text-center pt-2 mr-0">
        No Comps available
      </BFText>
    </View>
  )
}
